import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBranches } from '../context/BranchContext';
import { demoPosPath } from '../demoPosPaths';
import useAsync from '../hooks/useAsync';
import api from '../services/api';
import { BillStatus } from '../components/common/Badges';
import { EmptyState, Loader, Notice } from '../components/common/Feedback';
import { RefreshIcon, SearchIcon } from '../components/common/icons';
import { fmtDate } from '../utils/dates';
import { formatMoney, paidTotal } from '../utils/money';

const FILTERS = [
  { id: 'all', label: 'All', test: () => true },
  { id: 'open', label: 'Open', test: (b) => !['finalized', 'delivered'].includes(b.status) },
  { id: 'paid', label: 'Invoiced', test: (b) => ['finalized', 'delivered'].includes(b.status) },
];

const SOURCE_LABEL = { demo_engagement: 'Engagement', checkin: 'Check-in', manual: 'Walk-in' };

export default function Bills() {
  const navigate = useNavigate();
  const { param, loading: branchesLoading } = useBranches();
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const bills = useAsync(() => api.bills({ branches: param, limit: 200 }), [param], { enabled: !branchesLoading });

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const test = FILTERS.find((f) => f.id === filter).test;
    return (bills.data || []).filter((b) => {
      if (!test(b)) return false;
      if (!needle) return true;
      const hay = [b.customer?.name, b.customer?.phone, b.invoice?.invoice_number, b.franchise_name, ...(b.items || []).map((i) => i.name)];
      return hay.filter(Boolean).some((t) => String(t).toLowerCase().includes(needle));
    });
  }, [bills.data, filter, q]);

  return (
    <div className="dp-page">
      <div className="dp-page-head">
        <div>
          <h1>Bills</h1>
          <p className="dp-help">Bills of the ticked branches, plus every Demo Engagement bill when that is ticked.</p>
        </div>
        <button type="button" className="dp-btn dp-btn--ghost" onClick={() => bills.reload()} disabled={bills.loading}>
          <RefreshIcon size={16} /> Refresh
        </button>
      </div>

      <div className="dp-toolbar">
        <div className="dp-search">
          <SearchIcon size={16} />
          <input placeholder="Search customer, phone, service or invoice" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="dp-chips">
          {FILTERS.map((f) => (
            <button key={f.id} type="button" className={`dp-chip-btn${filter === f.id ? ' is-on' : ''}`} onClick={() => setFilter(f.id)}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {bills.error ? <Notice tone="error">{bills.error}</Notice> : null}
      {!bills.data && !bills.error ? (
        <Loader text="Loading bills…" />
      ) : rows.length ? (
        <div className="dp-table-wrap">
          <table className="dp-table dp-table--rows">
            <thead>
              <tr>
                <th>Created</th>
                <th>Customer</th>
                <th>Branch</th>
                <th>Services</th>
                <th className="num">Total</th>
                <th className="num">Paid</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((b) => (
                <tr key={b.id} onClick={() => navigate(demoPosPath(`bills/${b.id}`))} tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && navigate(demoPosPath(`bills/${b.id}`))}>
                  <td>{fmtDate(b.created_at)}</td>
                  <td>
                    <strong>{b.customer?.name || 'No customer yet'}</strong>
                    <small>{b.customer?.phone || ''}</small>
                  </td>
                  <td>
                    {b.franchise_name || '—'}
                    <small>{SOURCE_LABEL[b.source] || b.source}</small>
                  </td>
                  <td className="dp-cell-wrap">{(b.items || []).map((i) => i.name).join(', ') || '—'}</td>
                  <td className="num">{formatMoney(b.grand_total)}</td>
                  <td className="num">{formatMoney(paidTotal(b))}</td>
                  <td>
                    <BillStatus status={b.status} />
                    {b.invoice?.invoice_number ? <small>{b.invoice.invoice_number}</small> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState title="No bills" body="Create one from Today's queue." />
      )}
    </div>
  );
}
